class QuickViewDrawer extends HTMLElement {
  constructor() {
    super();
    this.sheet = this.querySelector('.qv-sheet');
    this.backdrop = this.querySelector('.qv-backdrop');
    this.content = this.querySelector('#QuickViewContent');
    this.loader = this.querySelector('.qv-loader');
    this.handle = this.querySelector('.qv-drag-handle');
    this.closeBtn = this.querySelector('.qv-close');
    this.indicatorIcon = this.querySelector('.qv-indicator .material-icons');
    this.isOpen = false;
    this.isDragging = false;
    this.hasMoved = false;
    this.startY = 0;
    this.currentY = 0;
    
    this.sheetHeight = 95;
    
    this.prevHandle = null;
    this.nextHandle = null;

    this.bindEvents = this.bindEvents.bind(this);
    if (this.backdrop && this.handle) {
      this.initEvents();
    }
  }

  t(key, defaultValue) {
    if (window.qvStrings && window.qvStrings[key]) return window.qvStrings[key];
    return defaultValue;
  }

  initEvents() {
    this.backdrop.addEventListener('click', () => this.close());
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });
    this.handle.addEventListener('click', (e) => {
      if (!this.hasMoved) {
        e.stopPropagation();
        this.close();
      }
      this.hasMoved = false;
    });

    this.bindEvents();
  }

  bindEvents() {
    this.handle.addEventListener('touchstart', this.dragStart.bind(this), { passive: false });
    document.addEventListener('touchmove', this.dragMove.bind(this), { passive: false });
    document.addEventListener('touchend', this.dragEnd.bind(this));
    this.handle.addEventListener('mousedown', this.dragStart.bind(this));
    document.addEventListener('mousemove', this.dragMove.bind(this));
    document.addEventListener('mouseup', this.dragEnd.bind(this));
  }

  toggleDrawerMode() {
    this.close();
  }

  setNavigation(prev, next) {
    this.prevHandle = prev;
    this.nextHandle = next;
  }

  getUniqueHandles() {
    const allBtns = Array.from(document.querySelectorAll('.quick-view-btn'));
    const handles = allBtns
      .map(b => b.dataset.handle)
      .filter(h => h && h.trim() !== '');
      
    return [...new Set(handles)];
  }

  async open(handle) {
    if (!handle) return;

    this.style.display = 'block';
    this.setAttribute('aria-hidden', 'false');
    const isDesktop = window.innerWidth > 768;
    
    this.sheetHeight = 95;
    
    requestAnimationFrame(() => {
      this.isOpen = true;
      this.setAttribute('open', '');
      if (!isDesktop) {
        this.updatePosition(this.sheetHeight);
      } else {
        this.sheet.style.transform = '';
      }
    });

    const hasContent = this.content.innerHTML.trim() !== "";
    if (hasContent) {
      this.content.classList.add('is-fading');
    } else {
      if (this.loader) this.loader.style.display = 'flex';
    }

    document.body.style.overflow = 'hidden';
    this.focus();

    try {
      const fetchPromise = fetch(`${window.Shopify.routes.root}products/${handle}?section_id=quick-view-drawer`).then(res => {
        if (!res.ok) throw new Error(`Erreur HTTP: ${res.status}`);
        return res.text();
      });

      const animationPromise = new Promise(resolve => {
        if (!hasContent) resolve();
        else setTimeout(resolve, 300);
      });

      const [text] = await Promise.all([fetchPromise, animationPromise]);
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      const html = doc.querySelector('.qv-product');

      if (this.loader) this.loader.style.display = 'none';

      if (html) {
        this.content.innerHTML = '';
        this.content.appendChild(html);

        const scrollContainer = isDesktop ? this.querySelector('.qv-content') : this.querySelector('.qv-scroll-container');
        if (scrollContainer) scrollContainer.scrollTop = 0;

        requestAnimationFrame(() => {
          this.content.classList.remove('is-fading');
        });

        const scripts = html.querySelectorAll('script');
        scripts.forEach(oldScript => {
            const newScript = document.createElement('script');
            Array.from(oldScript.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value));
            newScript.appendChild(document.createTextNode(oldScript.innerHTML));
            oldScript.parentNode.replaceChild(newScript, oldScript);
        });

        this.initProductLogic(html);
        this.initVideoLogic(html);
        this.injectSwipeListeners(html);
        this.updateDesktopNavigation(html);
        this.initJudgeMe(html);
        
        setTimeout(() => this.showSwipeTutorial(), 500);
      }
    } catch (e) {
      if (this.loader) this.loader.style.display = 'none';
      this.content.classList.remove('is-fading');
      this.content.innerHTML = '<p style="text-align:center;padding:40px">Error loading content.</p>';
    }
  }

  updateDesktopNavigation(container) {
    if (window.innerWidth > 768) {
        const prevBtn = container.querySelector('.qv-nav-btn.prev-btn');
        const nextBtn = container.querySelector('.qv-nav-btn.next-btn');
        if (this.prevHandle && prevBtn) {
          prevBtn.style.display = 'flex';
          prevBtn.onclick = (e) => { e.preventDefault(); this.navigateTo(this.prevHandle); };
        }
        if (this.nextHandle && nextBtn) {
          nextBtn.style.display = 'flex';
          nextBtn.onclick = (e) => { e.preventDefault(); this.navigateTo(this.nextHandle); };
        }
    } 
    
    const mobPrev = container.querySelector('.prev-mob');
    const mobNext = container.querySelector('.next-mob');
    
    if(mobPrev) {
        mobPrev.style.display = this.prevHandle ? 'flex' : 'none';
        mobPrev.onclick = (e) => { 
            e.preventDefault(); e.stopPropagation(); 
            this.navigateTo(this.prevHandle); 
        };
    }
    
    if(mobNext) {
        mobNext.style.display = this.nextHandle ? 'flex' : 'none';
        mobNext.onclick = (e) => { 
            e.preventDefault(); e.stopPropagation(); 
            this.navigateTo(this.nextHandle); 
        };
    }
  }

  showSwipeTutorial() {
    if (window.innerWidth > 768) return;

    if (!this.prevHandle && !this.nextHandle) return;
    try { if (sessionStorage.getItem('qv-swipe-seen') === 'true') return; } catch (e) {}
    if (this.querySelector('.qv-swipe-hint')) return;

    const hint = document.createElement('div');
    hint.className = 'qv-swipe-hint';
    const swipeText = this.t('swipe_hint', 'Swipe to change product');
    
    hint.innerHTML = `
        <div class="qv-hint-content">
            <span class="material-icons qv-hint-icon">touch_app</span>
            <span class="qv-hint-text">${swipeText}</span>
        </div>
    `;

    this.sheet.appendChild(hint);

    requestAnimationFrame(() => hint.classList.add('visible'));

    const removeHint = () => {
      hint.classList.remove('visible');
      setTimeout(() => hint.remove(), 500);
      try { sessionStorage.setItem('qv-swipe-seen', 'true'); } catch (e) {}
    };

    setTimeout(removeHint, 3500);
    hint.addEventListener('click', removeHint);
  }

  initVideoLogic(container) {
    const youtubeContainers = container.querySelectorAll('.qv-youtube-container');
    
    youtubeContainers.forEach(wrapper => {
      const cover = wrapper.querySelector('.qv-video-cover');
      
      if (cover) {
        cover.addEventListener('click', (e) => {
          e.stopPropagation();
          wrapper.classList.add('is-playing'); 
        });
      }
    });
  }

  close() {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    this.isOpen = false;
    this.removeAttribute('open');
    setTimeout(() => {
      this.setAttribute('aria-hidden', 'true');
    }, 50);
    if (window.innerWidth <= 768) {
      this.sheet.style.transform = `translateY(110%)`;
    }
    document.body.style.overflow = '';

    setTimeout(() => {
      this.style.display = 'none';
      this.sheetHeight = 95; 
      this.sheet.style.transform = '';
      this.prevHandle = null;
      this.nextHandle = null;
      this.content.innerHTML = '';
      this.content.classList.remove('is-fading');
      const hint = document.querySelector('.qv-swipe-hint');
      if (hint) hint.remove();
    }, 750);
  }

  injectSwipeListeners(container) {
    const contentArea = container.querySelector('.qv-content') || container;
    let touchStartX = 0;
    let touchStartY = 0;

    contentArea.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
      touchStartY = e.changedTouches[0].screenY;
    }, { passive: true });

    contentArea.addEventListener('touchend', (e) => {
      const touchEndX = e.changedTouches[0].screenX;
      const touchEndY = e.changedTouches[0].screenY;
      this.handleSwipeGesture(touchStartX, touchStartY, touchEndX, touchEndY);
    }, { passive: true });
  }

  handleSwipeGesture(startX, startY, endX, endY) {
    const threshold = 50;
    const xDiff = startX - endX;
    const yDiff = startY - endY;

    if (Math.abs(xDiff) > Math.abs(yDiff) && Math.abs(xDiff) > threshold) {
      if (xDiff > 0) {
        if (this.nextHandle) this.navigateTo(this.nextHandle);
      } else {
        if (this.prevHandle) this.navigateTo(this.prevHandle);
      }
    }
  }

  navigateTo(handle) {
    const uniqueHandles = this.getUniqueHandles();
    const index = uniqueHandles.indexOf(handle);

    if (index !== -1) {
      const prev = index > 0 ? uniqueHandles[index - 1] : null;
      const next = index < uniqueHandles.length - 1 ? uniqueHandles[index + 1] : null;
      this.setNavigation(prev, next);
    }
    this.open(handle);
  }

  initJudgeMe(container) {
    try {
      const badge = container.querySelector('.jdgm-prev-badge');
      if (badge) {
        badge.style.removeProperty('display');
      }

      if (window.jdgm) {
        if (typeof window.jdgm.on === 'function') {
          setTimeout(() => {
            window.jdgm.on('jdgm:loadAll', () => {});
            if (typeof window.jdgm.customWidgets?.load === 'function') {
              window.jdgm.customWidgets.load(container);
            }
          }, 100);
        }
        if (typeof window.jdgm.init === 'function') {
          setTimeout(() => window.jdgm.init(), 200);
        }
      }

      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('jdgm:loadBadges'));
        document.dispatchEvent(new CustomEvent('jdgm:loadAll'));
      }, 300);

    } catch (e) {
    }
  }

  initProductLogic(container) {
    const jsonScript = container.querySelector('.qv-product-json');
    if (!jsonScript) return;

    try {
      const variants = JSON.parse(jsonScript.textContent);
      const variantDataEl = container.querySelector('.qv-variant-data-json');
      const variantCustomData = variantDataEl ? JSON.parse(variantDataEl.textContent) : {};

      const selects = container.querySelectorAll('.qv-variant-select');
      const form = container.querySelector('form');
      const idInput = container.querySelector('.qv-variant-id');
      const priceEl = container.querySelector('.price-regular');
      const compareEl = container.querySelector('.price-compare');
      const swiperEl = container.querySelector('swiper-container');
      const addBtn = container.querySelector('.qv-add-to-cart');
      const btnText = addBtn ? addBtn.querySelector('.qv-add-text') : null;
      const cartIcon = addBtn ? addBtn.querySelector('.cart-icon') : null;
      const loadingIcon = addBtn ? addBtn.querySelector('.loading-icon') : null;
      const successIcon = addBtn ? addBtn.querySelector('.success-icon') : null;
      const preorderMsg = container.querySelector('.gPreorderTopMessage');

      if (!addBtn) return;

      const hasVariants = variants.length > 1 || (variants.length === 1 && variants[0].title !== "Default Title");

      const qtyInput = container.querySelector('.qv-qty-input');
      if (qtyInput) {
        container.querySelectorAll('.qv-qty-btn').forEach(btn => {
          btn.addEventListener('click', () => {
            let val = parseInt(qtyInput.value) || 1;
            let step = parseInt(qtyInput.step) || 1;
            let min = parseInt(qtyInput.min) || 1;

            if (btn.name === 'plus') {
              val += step;
            }
            else if (btn.name === 'minus') {
              val = Math.max(min, val - step);
            }

            qtyInput.value = val;
          });
        });
      }

      const storeAvailBtn = container.querySelector('.qv-store-availability-btn');
      if (storeAvailBtn) {
        storeAvailBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const productUrl = container.querySelector('.qv-view-full-link')?.getAttribute('href');
          if (productUrl) {
            window.location.href = productUrl + '#store-availability';
          }
        });
      }

      const updateVariant = () => {
        const selectedOptions = Array.from(selects).map(s => s.value);
        const matchedVariant = variants.find(v => {
          if (!v.options) return false;
          return v.options.every((opt, i) => opt === selectedOptions[i]);
        });

        if (matchedVariant) {
          idInput.value = matchedVariant.id;
          
          if (priceEl) priceEl.innerText = this.formatMoney(matchedVariant.price);
          if (compareEl) {
            if (matchedVariant.compare_at_price > matchedVariant.price) {
              compareEl.innerText = this.formatMoney(matchedVariant.compare_at_price);
              compareEl.style.display = 'inline';
            } else {
              compareEl.style.display = 'none';
            }
          }

          if (matchedVariant.featured_media && swiperEl && swiperEl.swiper) {
            const mediaId = matchedVariant.featured_media.id;
            const slides = Array.from(swiperEl.querySelectorAll('swiper-slide'));
            const slideIndex = slides.findIndex(slide => slide.dataset.mediaId == mediaId);
            if (slideIndex >= 0) swiperEl.swiper.slideToLoop(slideIndex);
          }

          const vData = variantCustomData[matchedVariant.id];
          const isPreorder = vData && vData.inventory_policy === 'continue' && vData.inventory_quantity <= 0;

          if (matchedVariant.available) {
            addBtn.removeAttribute('disabled');
            addBtn.setAttribute('aria-disabled', 'false');
            
            if (btnText) {
                const baseText = isPreorder ? window.qvStrings.preorder : window.qvStrings.addToCart;
                if (hasVariants && !isPreorder) {
                    btnText.innerText = `${baseText} • ${this.formatMoney(matchedVariant.price)}`;
                } else {
                    btnText.innerText = baseText;
                }
            }

            if (isPreorder && vData && vData.delivery_date) {
                preorderMsg.innerText = vData.delivery_date;
                preorderMsg.style.display = 'block';
            } else {
                preorderMsg.style.display = 'none';
            }

          } else {
            addBtn.setAttribute('disabled', 'true');
            addBtn.setAttribute('aria-disabled', 'true');
            if (btnText) btnText.innerText = window.qvStrings.soldOut;
            if (preorderMsg) preorderMsg.style.display = 'none';
          }
        } else {
          addBtn.setAttribute('disabled', 'true');
          addBtn.setAttribute('aria-disabled', 'true');
          if (btnText) btnText.innerText = window.qvStrings.unavailable;
          if (preorderMsg) preorderMsg.style.display = 'none';
        }
      };

      selects.forEach(s => s.addEventListener('change', updateVariant));
      
      if (hasVariants && variants.length > 0) {
          updateVariant();
      }

      if (form) {
        form.addEventListener('submit', async (e) => {
          e.preventDefault();
          if (!addBtn) return;

          addBtn.setAttribute('disabled', 'true');
          
          if (btnText) btnText.style.display = 'none';
          if (cartIcon) cartIcon.style.display = 'none';
          if (loadingIcon) loadingIcon.style.display = 'block';

          try {
            const formData = new FormData(form);

            const addResponse = await fetch(window.Shopify.routes.root + 'cart/add.js', {
              method: 'POST',
              body: formData
            });

            if (!addResponse.ok) throw new Error('Erreur ajout panier');
            const addData = await addResponse.json();

            if (!(window.innerWidth < 1024 && window.mobileCart && typeof window.mobileCart.openPeek === 'function')) {
                const cartResponse = await fetch(`${window.Shopify.routes.root}cart?section_id=main-cart&v=${Date.now()}`);
                const cartHTML = await cartResponse.text();
                const doc = new DOMParser().parseFromString(cartHTML, 'text/html');
                const currentDrawerContent = document.querySelector('#mini-cart .mini-cart__content, .mini-cart__content');
                const newItems = doc.querySelector('.cart-items-container, .cart-items, #main-cart-items');

                if (currentDrawerContent && newItems) {
                    const listContainer = currentDrawerContent.querySelector('.cart-items-container');
                    if (listContainer) listContainer.outerHTML = newItems.outerHTML;
                    else currentDrawerContent.innerHTML = newItems.outerHTML;
                    this.updateCartTotals(doc);
                    this.activateDrawerInteractions(currentDrawerContent);
                }
                document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            }

            setTimeout(() => {
                if (loadingIcon) loadingIcon.style.display = 'none';
                if (successIcon) successIcon.style.display = 'inline-block';
                if (btnText) {
                    btnText.innerText = window.qvStrings.added;
                    btnText.style.display = 'inline-block';
                }

                setTimeout(() => {
                    this.close();
                    
                    if (successIcon) successIcon.style.display = 'none';
                    if (cartIcon) cartIcon.style.display = 'inline-block';
                    updateVariant();
                    addBtn.removeAttribute('disabled');

                    if (window.innerWidth < 1024 && window.mobileCart && typeof window.mobileCart.openPeek === 'function') {
                        window.mobileCart.openPeek(addData);
                    } else {
                        const miniCart = document.getElementById('mini-cart');
                        if (miniCart) {
                            miniCart.setAttribute('open', '');
                            miniCart.classList.add('open');
                            if (typeof miniCart.open === 'function') miniCart.open();
                        } else {
                            const cartIcon = document.querySelector('a[href="/cart"], .header__icon--cart');
                            if (cartIcon) cartIcon.click();
                        }
                    }
                }, 1500); 

            }, 800);

          } catch (err) {
            if (loadingIcon) loadingIcon.style.display = 'none';
            if (cartIcon) cartIcon.style.display = 'inline-block';
            if (btnText) btnText.style.display = 'inline-block';
            addBtn.removeAttribute('disabled');
          }
        });
      }
    } catch (e) {
    }
  }

  updateCartTotals(doc) {
    const selectors = ['#cart-total-price', '.cart-total-price', '.cart-summary__total .money', '.cart-summary__subtotal', '.cart-summary__free-shipping', '.cart-summary__header'];
    selectors.forEach(selector => {
      const newEl = doc.querySelector(selector);
      const currentEls = document.querySelectorAll(selector);
      if (newEl && currentEls.length) {
        currentEls.forEach(currentEl => currentEl.innerHTML = newEl.innerHTML);
      }
    });

    const newCount = doc.querySelector('.cart-count, #cart-icon-bubble');
    const currentCount = document.querySelector('.cart-count, #cart-icon-bubble');
    if (newCount && currentCount) {
      currentCount.innerHTML = newCount.innerHTML;
      currentCount.classList.remove('hidden');
    }
  }

  activateDrawerInteractions(container) {
    if (!container) return;

    const handleUpdate = async (key, quantity, itemRow) => {
      if (!key) return;
      if (quantity === 0 && itemRow) {
        itemRow.style.opacity = "0";
        setTimeout(() => itemRow.style.display = "none", 300);
      } else {
        container.style.opacity = '0.6';
      }
      container.style.pointerEvents = 'none';

      try {
        await fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: quantity })
        });

        const res = await fetch(`${window.Shopify.routes.root}cart?section_id=main-cart&v=${Date.now()}`);
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const newItems = doc.querySelector('.cart-items-container, .cart-items, #main-cart-items');

        if (newItems) {
          let list = container.querySelector('.cart-items-container');
          if (list) list.outerHTML = newItems.outerHTML;
          else container.innerHTML = newItems.outerHTML;
          this.updateCartTotals(doc);
          this.activateDrawerInteractions(container);
        }
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } catch (e) {
        if (itemRow) itemRow.style.opacity = "1";
      } finally {
        container.style.opacity = '1';
        container.style.pointerEvents = 'auto';
      }
    };

    container.querySelectorAll('.cart-item__remove, .cart-item__remove-wrapper button').forEach(btn => {
      const cleanBtn = btn.cloneNode(true);
      if (btn.parentNode) btn.parentNode.replaceChild(cleanBtn, btn);

      cleanBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const item = cleanBtn.closest('.cart-item');
        const key = item ? (item.getAttribute('data-item-id') || item.dataset.key || item.dataset.variantId) : null;
        handleUpdate(key, 0, item);
      });
    });
  }

  dragStart(e) {
    if (!this.isOpen || window.innerWidth > 768) return;
    this.isDragging = true;
    this.hasMoved = false;
    this.startY = e.type.includes('mouse') ? e.clientY : e.touches[0].clientY;
    this.sheet.style.transition = 'none';
  }

  dragMove(e) {
    if (!this.isDragging || window.innerWidth > 768) return;
    const y = e.type.includes('mouse') ? e.clientY : e.touches[0].clientY;
    const delta = this.startY - y;

    if (delta > 0 && this.sheetHeight >= 95) return;

    if (Math.abs(delta) > 5) this.hasMoved = true;

    const vh = window.innerHeight;
    let newHeight = 95 + (delta / vh * 100); 

    this.updatePosition(newHeight);
  }

  dragEnd() {
    if (!this.isDragging || window.innerWidth > 768) return;
    this.isDragging = false;
    if (!this.hasMoved) return;

    this.sheet.style.transition = '';

    if (this.currentHeight < 75) {
      this.close();
    } else {
      this.sheetHeight = 95;
      this.updatePosition(95);
    }
  }

  updatePosition(heightPerc) {
    this.currentHeight = heightPerc;
    const hiddenH = 100 - heightPerc;
    this.sheet.style.transform = `translateY(${Math.max(0, hiddenH)}%)`;
  }

  formatMoney(cents) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ? window.Shopify.currency.active : 'EUR';
    const lang = document.documentElement.lang || 'fr-FR';

    return (cents / 100).toLocaleString(lang, {
      style: 'currency',
      currency: currency
    });
  }
}

if (!customElements.get('quick-view-drawer')) {
  customElements.define('quick-view-drawer', QuickViewDrawer);
}

document.addEventListener('click', (e) => {
  const btn = e.target.closest('.quick-view-btn');
  
  if (btn && btn.dataset.handle && btn.dataset.handle.trim() !== '') {
    e.preventDefault();
    e.stopPropagation();
    const handle = btn.dataset.handle;
    const drawer = document.getElementById('QuickViewDrawer');
    if (drawer) {
      const uniqueHandles = drawer.getUniqueHandles();
      const index = uniqueHandles.indexOf(handle);
      const prev = index > 0 ? uniqueHandles[index - 1] : null;
      const next = index < uniqueHandles.length - 1 ? uniqueHandles[index + 1] : null;

      drawer.setNavigation(prev, next);
      drawer.open(handle);
    }
  }
});